import styled from "@emotion/styled";
import { CheckCircle, Circle, Close, Star } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Modal,
  Radio,
  Snackbar,
  TextField,
  Typography
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addSubscription, addCoupon } from "../../api/userApi";
import { selectCurrentUser } from "../../features/auth/authSelectors";
import { useCurrentUser } from "../Extra/UserContext";
import dayjs from "dayjs";
import { logoutUser } from "../../features/auth/authSlice";
import {
  allowedCountries,
  countryRates
} from "../../utils/countryRatesMapping";

const StyledTextField = styled(TextField)(({ theme }) => ({
  input: {
    color: "#FCFCFC"
  },
  "& .MuiOutlinedInput-root": {
    border: "2px dotted #A4A4A4",
    "& fieldset": {
      color: "#9CA3AF"
    },
    "&:hover fieldset": {
      borderColor: "#FCFCFC",
      border: "none"
    },
    "&.Mui-focused fieldset": {
      color: "#9CA3AF",
      borderColor: "#FCFCFC",
      border: "none"
    }
  }
}));

const SubscriptionModal = ({ open, onClose, isMobile = false }) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputCouponValue, setInputCouponValue] = useState("");
  const user = useSelector(selectCurrentUser);
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();

  const freeTrialExpired = dayjs().diff(dayjs(currentUser?.createdAt), "days");
  const showDiscountedYearly = freeTrialExpired + 1 < 10;

  const dontAddProgram = showDiscountedYearly ? false : true;

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleSubscribe = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const { session } = await addSubscription(
        "yearly-discounted",
        user?.email,
        dontAddProgram
      );
      if (session?.url) window.location.href = session?.url;
      else {
        setSnackbarMessage(session.message);
        setOpenSnackbar(true);
        setTimeout(() => {
          onClose();
          handleCloseSnackbar();
        }, 3000);
      }
    } catch (err) {
      setSnackbarMessage(err.error);
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const currentCurrency = useMemo(
    () =>
      countryRates[
        allowedCountries?.includes(user?.country) ? user?.country : "US"
      ],
    [user]
  );
  const mainContent = () => {
    const price = showDiscountedYearly
      ? `${currentCurrency?.symbol}${
          Number.isInteger(currentCurrency?.rate * 99)
            ? currentCurrency?.rate * 99
            : (currentCurrency?.rate * 99)?.toFixed(2)
        }`
      : `${currentCurrency?.symbol}${
          Number.isInteger(currentCurrency?.rate * 129)
            ? currentCurrency?.rate * 129
            : (currentCurrency?.rate * 129)?.toFixed(2)
        }`;
    const badge = showDiscountedYearly ? "Special Offer" : null;
    const expiry = showDiscountedYearly
      ? `Offer expires in ${10 - freeTrialExpired} days!`
      : null;

    return (
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#2B2B2B",
          textAlign: "center",
          borderRadius: "8px",
          margin: 0,
          position: "relative",
          border: "2px solid #368AD0"
        }}
      >
        {/* Badge if provided */}
        {badge && (
          <Box
            display="flex"
            justifyContent="end"
            width="100%"
            alignItems="center"
            sx={{
              position: "absolute",
              top: 0,
              right: 0
            }}
          >
            <Typography
              display="flex"
              sx={{
                background: "#FCFCFC",
                padding: "3px 4px",
                alignItems: "center",
                gap: "5px",
                justifyContent: "center",
                borderRadius: "0 8px 0 8px"
              }}
              color="black"
            >
              <Star sx={{ fill: "#F1B44C", fontSize: "12px" }} />
              <Typography variant="caption" fontSize="10px" lineHeight={1}>
                {badge}
              </Typography>
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            padding: "16px",
            justifyContent: "space-between"
          }}
        >
          {/* Top section - Radio and Pricing */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              mt: badge ? 2 : 0
            }}
          >
            {/* <Radio
              checked={true}
              value={subscriptionType}
              name={`radio-${subscriptionType}`}
              icon={<Circle sx={{ fill: "#FCFCFC" }} />}
              checkedIcon={<CheckCircle sx={{ fill: "#368AD0" }} />}
              sx={{ p: 0, mr: 1 }}
            /> */}
            <Typography variant="h6" fontWeight="bold" color="#368AD0">
              {`${price}`}
            </Typography>
          </Box>

          {/* Middle section - Description */}
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="body2" fontSize="12px" color="#FCFCFC">
              – Daily MOD Access
            </Typography>
            <Typography variant="body2" fontSize="12px" mt={1} color="#FCFCFC">
              – One Year Diary Logging
            </Typography>
            <Typography variant="body2" fontSize="12px" mt={1} color="#FCFCFC">
              – Full #800gChallenge Program (30 Days)
            </Typography>
            {/* <Typography variant="body2" fontSize="12px" mt={1} color="#FCFCFC">
              –{" "}
              {showDiscountedYearly
                ? "Special discount pricing"
                : "Complete yearly access"}
            </Typography> */}

            {expiry && (
              <Box mt={2}>
                <Typography variant="caption" textAlign="center" color="error">
                  {expiry}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Bottom section - No refunds text */}
          <Box>
            <Typography variant="caption" color="#8A8A8A" fontStyle="italic">
              No refunds. Cancel anytime.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // Mobile Layout
  if (isMobile) {
    return open ? (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 10,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1300,
          overflowY: "auto"
        }}
      >
        <Box
          sx={{
            position: "relative",
            top: "30%",
            left: "50%",
            maxHeight: "95vh",
            overflowY: "auto",
            overflow: "auto",
            width: { md: "55%", xs: "100%" },
            borderRadius: 2,
            transform: "translate(-50%, -50%)"
          }}
        >
          <Box
            sx={{
              padding: { xs: "28px", md: 0 },
              position: "relative",
              bgcolor: "#000",
              borderRadius: "16px 16px 0 0",
              boxShadow: 24
            }}
            width="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: "url(/images/sky.png)",
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: 0.5,
                zIndex: 1
              }}
            />
            <Box
              sx={{
                position: "relative",
                zIndex: 2,
                width: "100%"
              }}
            >
              <Box
                display="flex"
                justifyContent="end"
                alignItems="flex-end"
                width="100%"
              >
                <IconButton
                  sx={{
                    svg: {
                      fill: "#FCFCFC"
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
              <Box
                sx={{ padding: { sm: 0, md: "0 95px" } }}
                display="flex"
                gap="10px"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                <Box
                  component="img"
                  src="/images/threepillars.png"
                  alt="base"
                  sx={{
                    width: "68px",
                    height: "68px",
                    objectFit: "cover"
                  }}
                />
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap="10px"
                  flexDirection="column"
                >
                  <Typography fontWeight={600} fontSize="21px" color="#FCFCFC">
                    Keep Going!
                  </Typography>
                  <Typography
                    textAlign="center"
                    fontWeight={400}
                    fontSize="14px"
                    color="#FCFCFC"
                  >
                    Subscribe to access full features and training programs
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              background: "#333333",
              display: "flex",
              padding: "24px 47px",
              borderRadius: "0 0 16px 16px",
              flexDirection: "column"
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "30px",
                flexWrap: "nowrap",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {mainContent()}
            </Box>
            <Box mt={3}>
              <Button
                sx={{
                  background: "#368AD0",
                  color: "#FCFCFC",
                  textTransform: "none",
                  width: "100%",
                  fontWeight: "bold"
                }}
                onClick={handleSubscribe}
              >
                Subscribe
              </Button>
            </Box>
          </Box>
          <Snackbar
            open={openSnackbar}
            autoHideDuration={3000}
            onClose={handleCloseSnackbar}
            sx={{ top: 0 }}
          >
            <Alert
              severity={
                snackbarMessage?.includes("success" || "Success")
                  ? "success"
                  : "error"
              }
              onClose={handleCloseSnackbar}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    ) : (
      <></>
    );
  }

  // Desktop Layout
  return (
    <Modal open={open} onClose={onClose} disableAutoFocus disableEnforceFocus>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          height: { xs: "80%", md: "auto" },
          width: { md: "35%", xs: "100%" },
          borderRadius: 2,
          border: "none",
          transform: "translate(-50%, -50%)"
        }}
      >
        <Box
          sx={{
            padding: { xs: "28px", md: 0 },
            position: "relative",
            bgcolor: "#000",
            borderRadius: "16px 16px 0 0",
            boxShadow: 24,
            overflow: "hidden"
          }}
          display="flex"
          width="100%"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: "url(/images/sky.png)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: { xs: 0.5, md: 0.5 },
              zIndex: 1
            }}
          />
          <Box
            sx={{
              position: "relative",
              width: "100%",
              zIndex: 2
            }}
          >
            <Box
              display="flex"
              justifyContent="end"
              alignItems="flex-end"
              width="100%"
            >
              <IconButton
                sx={{
                  svg: {
                    fill: "#FCFCFC"
                  }
                }}
                onClick={onClose}
              >
                <Close />
              </IconButton>
            </Box>
            <Box
              sx={{ padding: { sm: 0, md: "10px 95px" } }}
              display="flex"
              gap="10px"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Box
                component="img"
                src="/images/threepillars.png"
                alt="base"
                sx={{
                  width: "68px",
                  height: "68px",
                  objectFit: "cover"
                }}
              />
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap="10px"
                flexDirection="column"
              >
                <Typography
                  textAlign="center"
                  fontWeight={500}
                  fontSize="34px"
                  color="#FCFCFC"
                >
                  Keep Going!
                </Typography>

                <Typography
                  mt={1}
                  fontWeight={400}
                  fontSize="14px"
                  textAlign="center"
                  color="#FCFCFC"
                >
                  Subscribe to access full features and training programs
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            background: "#333333",
            display: "flex",
            padding: "24px 47px",
            borderRadius: "0 0 16px 16px",
            flexDirection: "column"
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              width: "100%"
            }}
          >
            {mainContent()}
          </Box>

          <Box mt={4} display="flex" justifyContent="center">
            <Button
              sx={{
                width: { xs: "100%", md: "70%" },
                backgroundColor: "#368AD0",
                textTransform: "none",
                fontWeight: "bold"
              }}
              variant="contained"
              onClick={handleSubscribe}
            >
              Subscribe
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SubscriptionModal;
